import React from 'react';
import { isEqual } from 'lodash';
import { useParams } from 'react-router';
import { Route } from '@reverbdotcom/commons/src/route_settings';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import CSPGridDataManager from '../data_managers/csp_grid_data_manager';
import { CollectionHeaderStore, CollectionHeaderActions } from '@reverbdotcom/commons/src/components/stores/collection_header_store';
import CuratedSetRowActions from '../components/discovery/actions/curated_set_row_actions';

function alreadyLoadedHeader(newParams) {
  const storeParams = CollectionHeaderStore.getState().params;
  return storeParams && storeParams.curated_set_slug === newParams.curated_set_slug;
}

function curatedSetRowActions(excludedSlug) {
  CuratedSetRowActions.fetch({
    sort: 'random',
    csp_type: true,
    excluded_slug: excludedSlug,
  });
}

const lazyCollectionPage = async () => {
  const CSPCuratedSetGrid = (await import(
    /* webpackChunkName: 'csp' */
    '../components/discovery/csp/csp_curated_set_grid'
  )).default;

  // Replaces the v3 onEnter/onChange route hooks: the first effect run is
  // route entry, later runs (param or query changes) are the v3 onChange.
  // The data managers dedupe repeat fetches by comparing params.
  return {
    Component: function CollectionPage() {
      const params = useParams();
      const url = useURL();
      const previousState = React.useRef(null);

      React.useEffect(() => {
        const routerState = { params, location: { query: url.query } };
        const oldState = previousState.current;
        previousState.current = routerState;

        curatedSetRowActions(params.curated_set_slug);

        if (!oldState) {
          if (!alreadyLoadedHeader(params)) {
            CollectionHeaderActions.fetchOrClear(params);
          }
          CSPGridDataManager.fetchCSPs(routerState);
          return;
        }

        if (!isEqual(oldState.params, params)) {
          CollectionHeaderActions.fetchOrClear(params);
        }
        CSPGridDataManager.fetchCSPs(routerState, oldState);
      }, [params.curated_set_slug, url.query]);

      return <CSPCuratedSetGrid />;
    },
  };
};

const CollectionRoute = (
  <Route
    key="collection/:curated_set_slug"
    path="collection/:curated_set_slug"
    handle={{ clientSide: true }}
    lazy={lazyCollectionPage}
  />
);

export default CollectionRoute;
